import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogoUpload } from "./LogoUpload";

const EMP_TYPES = ["Full Time", "Part Time", "Remote", "Internship"];
const CATEGORIES = ["Engineering", "Design", "Analytics", "Marketing", "Sales", "Human Resources", "Finance", "Operations", "Other"];
const empty = { title: "", company: "", location: "", salary_range: "", experience: "", employment_type: "Full Time", category: "Engineering", description: "", logo_path: null, apply_by: "" };
const isExpired = (d) => d && d < new Date().toISOString().slice(0, 10);

export function JobsManager({ onChanged }) {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => toast.error("Failed to load jobs"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (j) => { setForm({ ...empty, ...j }); setEditing(j.id); };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.keys(empty).map((k) => [k, form[k] === "" && k === "apply_by" ? null : form[k]]));
      if (editing === "new") await api.post("/admin/jobs", payload);
      else await api.put(`/admin/jobs/${editing}`, payload);
      toast.success(editing === "new" ? "Job posted" : "Job updated");
      setEditing(null); load(); onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete "${j.title}"?`)) return;
    try {
      await api.delete(`/admin/jobs/${j.id}`);
      toast.success("Job deleted"); load(); onChanged?.();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="p-4 lg:p-5 flex items-center justify-between border-b border-slate-100">
        <p className="text-sm text-slate-600">{jobs.length} job{jobs.length === 1 ? "" : "s"} live on the site</p>
        <Button data-testid="add-job-button" onClick={openNew} className="rounded-full bg-gradient-to-r from-blue-700 to-indigo-600 text-white">
          <Plus size={15} className="mr-1.5" /> Add Job
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-jobs-table" className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              <th className="px-5 py-3">Title</th><th className="px-5 py-3">Company</th><th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Type</th><th className="px-5 py-3">Salary</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500">No jobs yet.</td></tr>
            ) : jobs.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    {j.logo_path && <img src={`${BACKEND_URL}/api/logos/${j.logo_path}`} alt="" className="w-8 h-8 rounded-lg object-contain border border-slate-100 bg-white" />}
                    <div>
                      <p className="font-semibold text-slate-900">{j.title}</p>
                      <p className="text-xs text-slate-500">{j.category} · {j.experience}{j.apply_by && <span className={isExpired(j.apply_by) ? "text-rose-600" : ""}> · {isExpired(j.apply_by) ? "Expired" : "Apply by"} {j.apply_by}</span>}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-slate-700">{j.company}</td>
                <td className="px-5 py-4 text-slate-700">{j.location}</td>
                <td className="px-5 py-4 text-slate-700">{j.employment_type}</td>
                <td className="px-5 py-4 text-slate-700">{j.salary_range}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <Button data-testid={`edit-job-${j.id}`} size="sm" variant="outline" className="rounded-full mr-2" onClick={() => openEdit(j)}><Pencil size={13} /></Button>
                  <Button data-testid={`delete-job-${j.id}`} size="sm" variant="outline" className="rounded-full text-rose-600 hover:bg-rose-50" onClick={() => remove(j)}><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="job-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "Post a new job" : "Edit job"}</DialogTitle></DialogHeader>
          <form onSubmit={save} data-testid="job-form" className="grid md:grid-cols-2 gap-x-5 gap-y-4 mt-2">
            <F label="Job Title *"><Input data-testid="job-title" value={form.title} onChange={set("title")} required /></F>
            <F label="Company *"><Input data-testid="job-company" value={form.company} onChange={set("company")} required /></F>
            <F label="Location *"><Input data-testid="job-location" value={form.location} onChange={set("location")} placeholder="e.g. Pune, IN / Remote" required /></F>
            <F label="Salary Range *"><Input data-testid="job-salary" value={form.salary_range} onChange={set("salary_range")} placeholder="e.g. ₹10L - ₹15L" required /></F>
            <F label="Experience *"><Input data-testid="job-experience" value={form.experience} onChange={set("experience")} placeholder="e.g. 2-4 years" required /></F>
            <F label="Employment Type *">
              <Select value={form.employment_type} onValueChange={set("employment_type")}>
                <SelectTrigger data-testid="job-type" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Category *">
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="job-category" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Apply-by deadline"><Input data-testid="job-apply-by" type="date" value={form.apply_by || ""} onChange={set("apply_by")} /></F>
            <F label="Company logo"><LogoUpload value={form.logo_path} onChange={(p) => setForm((s) => ({ ...s, logo_path: p }))} /></F>
            <F label="Description *" full><Textarea data-testid="job-description" value={form.description} onChange={set("description")} rows={4} required /></F>
            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" data-testid="job-save-button" disabled={saving} className="rounded-full bg-gradient-to-r from-blue-700 to-indigo-600 text-white">{saving ? "Saving…" : editing === "new" ? "Post Job" : "Save Changes"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
